import { useState, useEffect, useCallback } from 'react';
import { availabilityService } from '@/services/availabilityService';
import type { AvailabilitySlot, AvailabilityTemplate } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useAvailability(from?: string, to?: string) {
  const { user } = useUnifiedAuth();
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [templates, setTemplates] = useState<AvailabilityTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSlots = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await availabilityService.list(from, to);
      setSlots(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading availability');
    } finally {
      setLoading(false);
    }
  }, [user, from, to]);

  const fetchTemplates = useCallback(async () => {
    if (!user) return;
    try {
      const data = await availabilityService.getTemplates();
      setTemplates(data);
    } catch (_err) { /* silent */ }
  }, [user]);

  useEffect(() => { fetchSlots(); fetchTemplates(); }, [fetchSlots, fetchTemplates]);

  const createSlots = useCallback(async (newSlots: Partial<AvailabilitySlot>[]) => {
    const data = await availabilityService.create(newSlots);
    setSlots(prev => [...prev, ...data]);
    return data;
  }, []);

  const updateSlot = useCallback(async (id: string, updates: Partial<AvailabilitySlot>) => {
    const data = await availabilityService.update(id, updates);
    setSlots(prev => prev.map(s => s.id === id ? data : s));
    return data;
  }, []);

  const removeSlot = useCallback(async (id: string) => {
    await availabilityService.remove(id);
    setSlots(prev => prev.filter(s => s.id !== id));
  }, []);

  const saveTemplates = useCallback(async (newTemplates: Partial<AvailabilityTemplate>[]) => {
    const data = await availabilityService.saveTemplates(newTemplates);
    setTemplates(data);
    return data;
  }, []);

  const applyTemplate = useCallback(async (fromDate: string, toDate: string) => {
    const data = await availabilityService.applyTemplate(fromDate, toDate);
    await fetchSlots();
    return data;
  }, [fetchSlots]);

  return {
    slots, templates, loading, error,
    refetchSlots: fetchSlots, createSlots, updateSlot, removeSlot,
    saveTemplates, applyTemplate,
  };
}
